import {
    edgeState, itemTarget, scrolledForThumbOffset, thumbGeometry,
} from '../../../ts/howspace/scroll-control/scroll-geometry';

howspace.components.scrollControl = (() => {
    'use strict';

    const CONTROL_CLASS = 'howspace-scroll-control';
    const INITIALIZED_CLASS = 'howspace-scroll-control-initialized';

    // Elements, listeners and observers per control, so destroy() detaches exactly what init() attached.
    const attached = new WeakMap();

    // Narrow enough to show a long board's position, wide enough to grab with a finger.
    const MIN_THUMB_WIDTH = 32;

    const isRtl = scroller => getComputedStyle(scroller).direction === 'rtl';

    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Right to left, the browser counts scrollLeft down from zero. The distance from the start edge is the same number
    // either way, which keeps every calculation one-sided.
    const metricsOf = scroller => ({
        scrolled: Math.abs(scroller.scrollLeft),
        scrollWidth: scroller.scrollWidth,
        clientWidth: scroller.clientWidth,
    });

    const scrollToDistance = (scroller, distance, rtl, behavior) => {
        scroller.scrollTo({left: rtl ? -distance : distance, behavior});
    };

    // The columns or phases an arrow steps through, by default the scroller's own children.
    const itemExtentsOf = (control, scroller, rtl) => {
        const selector = control.dataset.scrollItems;
        const items = selector ? scroller.querySelectorAll(selector) : scroller.children;
        const box = scroller.getBoundingClientRect();
        const scrolled = Math.abs(scroller.scrollLeft);

        return [...items]
            .filter(item => item.offsetParent !== null)
            .map(item => {
                const rect = item.getBoundingClientRect();
                const start = (rtl ? box.right - rect.right : rect.left - box.left) + scrolled;
                return {start, end: start + rect.width};
            });
    };

    const setDisabled = (button, disabled) => {
        button.classList.toggle('disabled', disabled);
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    };

    // Runs on every scroll event, so it only writes. The track width it needs is measured by the resize observer.
    const sync = state => {
        const {control, scroller, strip, thumb, startButton, endButton} = state;
        const metrics = metricsOf(scroller);
        const edges = edgeState(metrics);

        control.classList.toggle('can-scroll-start', edges.canScrollToStart);
        control.classList.toggle('can-scroll-end', edges.canScrollToEnd);

        // A board that stops overflowing takes the strip away. Hiding a focused arrow would drop focus to the body
        // and send the next tab back to the top of the page, so the board takes focus instead.
        if (!edges.overflows && strip.contains(document.activeElement)) {
            scroller.focus({preventScroll: true});
        }
        if (!edges.overflows) {
            strip.hidden = true;
            return;
        }

        // A hidden track has no width, so it is measured the moment the strip appears, ahead of the resize observer.
        if (strip.hidden) {
            strip.hidden = false;
            state.trackWidth = state.track.clientWidth;
        }

        // The ends are greyed out rather than disabled, so the arrow that was just used keeps focus.
        setDisabled(startButton, !edges.canScrollToStart);
        setDisabled(endButton, !edges.canScrollToEnd);

        const {width, offset} = thumbGeometry(metrics, state.trackWidth, MIN_THUMB_WIDTH);
        thumb.style.width = `${width}px`;
        thumb.style.insetInlineStart = `${offset}px`;
    };

    // The scroll padding is the room the fades take, which the column snapping keeps clear as well.
    const step = (state, direction) => {
        const {control, scroller} = state;
        const style = getComputedStyle(scroller);
        const rtl = style.direction === 'rtl';
        const target = itemTarget(
            itemExtentsOf(control, scroller, rtl),
            metricsOf(scroller),
            direction,
            parseFloat(style.scrollPaddingInlineStart) || 0,
            parseFloat(style.scrollPaddingInlineEnd) || 0,
        );
        scrollToDistance(scroller, target, rtl, prefersReducedMotion() ? 'instant' : 'smooth');
    };

    // Grabbing the thumb drags it from where it was grabbed. Pressing the track elsewhere moves the thumb's middle
    // under the pointer first, then drags from there, the way a native scrollbar answers a press on its track.
    // One drag at a time. A second finger would save the snap setting the first one had already turned off,
    // then restore it last and leave the board without its column snapping.
    const startDrag = (state, event) => {
        if (event.button !== 0 || !event.isPrimary || state.stopDrag !== null) {
            return;
        }
        const {control, scroller, track, thumb} = state;
        const rtl = isRtl(scroller);
        const pointerAt = pointerEvent => {
            const box = track.getBoundingClientRect();
            return rtl ? box.right - pointerEvent.clientX : pointerEvent.clientX - box.left;
        };
        const thumbWidth = thumb.offsetWidth;
        const thumbStart = parseFloat(thumb.style.insetInlineStart) || 0;
        const grabbedAt = event.target === thumb ? pointerAt(event) - thumbStart : thumbWidth / 2;

        const follow = pointerEvent => {
            const offset = pointerAt(pointerEvent) - grabbedAt;
            const distance = scrolledForThumbOffset(offset, metricsOf(scroller), state.trackWidth, thumbWidth);
            scrollToDistance(scroller, distance, rtl, 'instant');
        };

        // Snapping would pull the board back to a column on every move, so it waits until the thumb is let go.
        const snapType = scroller.style.scrollSnapType;
        scroller.style.scrollSnapType = 'none';
        control.classList.add('is-dragging');

        // Losing the capture follows every release and cancel, and also a capture taken away mid-drag.
        const stop = () => {
            track.removeEventListener('pointermove', follow);
            track.removeEventListener('lostpointercapture', stop);
            scroller.style.scrollSnapType = snapType;
            control.classList.remove('is-dragging');
            state.stopDrag = null;
        };
        track.addEventListener('pointermove', follow);
        track.addEventListener('lostpointercapture', stop);
        state.stopDrag = stop;

        track.setPointerCapture(event.pointerId);
        event.preventDefault();
        follow(event);
    };

    return {
        init: elem => {
            elem.findIncludingSelf(`.${CONTROL_CLASS}:not(.${INITIALIZED_CLASS})`).each(function () {
                const control = this;
                const state = {
                    control,
                    scroller: control.querySelector('.howspace-scroll-control-scroller'),
                    strip: control.querySelector('.howspace-scroll-control-strip'),
                    track: control.querySelector('.howspace-scroll-control-track'),
                    thumb: control.querySelector('.howspace-scroll-control-thumb'),
                    startButton: control.querySelector('.howspace-scroll-control-btn.start'),
                    endButton: control.querySelector('.howspace-scroll-control-btn.end'),
                    trackWidth: 0,
                    stopDrag: null,
                };
                const required = ['scroller', 'strip', 'track', 'thumb', 'startButton', 'endButton'];
                if (required.some(part => state[part] === null)) {
                    return;
                }

                control.classList.add(INITIALIZED_CLASS);

                const {scroller, track, startButton, endButton} = state;
                state.onScroll = () => sync(state);
                state.onStartClick = () => startButton.getAttribute('aria-disabled') !== 'true' && step(state, -1);
                state.onEndClick = () => endButton.getAttribute('aria-disabled') !== 'true' && step(state, 1);
                state.onTrackPointerDown = event => startDrag(state, event);

                startButton.addEventListener('click', state.onStartClick);
                endButton.addEventListener('click', state.onEndClick);
                track.addEventListener('pointerdown', state.onTrackPointerDown);
                scroller.addEventListener('scroll', state.onScroll, {passive: true});

                // Watching the content as well as the scroller. A Kanban column collapsing, a window narrow enough
                // to shrink the columns, or a new phase all change the width inside a scroller that keeps its own size,
                // and some of them change no attribute the scroller can see. The track is watched too,
                // because its width decides how far the thumb can travel.
                state.resizeObserver = new ResizeObserver(() => {
                    state.trackWidth = track.clientWidth;
                    sync(state);
                });
                const observeContent = () => {
                    state.resizeObserver.disconnect();
                    state.resizeObserver.observe(scroller);
                    state.resizeObserver.observe(track);
                    for (const child of scroller.children) {
                        state.resizeObserver.observe(child);
                    }
                };
                observeContent();

                // Columns coming and going are the one change that alters which elements there are to watch.
                state.mutationObserver = new MutationObserver(() => {
                    observeContent();
                    sync(state);
                });
                state.mutationObserver.observe(scroller, {childList: true});

                attached.set(control, state);
                sync(state);
            });
        },
        destroy: elem => {
            elem.findIncludingSelf(`.${CONTROL_CLASS}.${INITIALIZED_CLASS}`).each(function () {
                const control = this;
                const state = attached.get(control);

                control.classList.remove(INITIALIZED_CLASS);

                if (state === undefined) {
                    return;
                }

                state.stopDrag?.();
                state.scroller.removeEventListener('scroll', state.onScroll);
                state.startButton.removeEventListener('click', state.onStartClick);
                state.endButton.removeEventListener('click', state.onEndClick);
                state.track.removeEventListener('pointerdown', state.onTrackPointerDown);
                state.resizeObserver.disconnect();
                state.mutationObserver.disconnect();
                attached.delete(control);
            });
        },
    };
})();
